import React, { createContext, useContext, useState, useMemo, ReactNode } from "react";
import { SearchResponseClass } from "../types/search";

// Define the context state interface
interface ResultContextState {
	searchResponse: SearchResponseClass | null;
	setSearchResponse: (response: SearchResponseClass | null) => void;
	isLoading: boolean;
	setIsLoading: (loading: boolean) => void;
	error: string | null;
	setError: (error: string | null) => void;
	selectedResourceId: string | null;
	setSelectedResourceId: (id: string | null) => void;
	initialTab: string | null;
	setInitialTab: (tab: string | null) => void;
}

// Create the context with default values
const ResultContext = createContext<ResultContextState | undefined>(undefined);

// Provider component
export const ResultProvider: React.FC<{ children: ReactNode }> = ({
	children,
}) => {
	const [searchResponse, setSearchResponse] =
		useState<SearchResponseClass | null>(null);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [selectedResourceId, setSelectedResourceId] = useState<string | null>(
		null,
	);
	const [initialTab, setInitialTab] = useState<string | null>(null);
	
	// Memoize the context value to prevent unnecessary re-renders
	const value = useMemo(
		() => ({
			searchResponse,
			setSearchResponse,
			isLoading,
			setIsLoading,
			error,
			setError,
			selectedResourceId,
			setSelectedResourceId,
			initialTab,
			setInitialTab,
		}),
		[searchResponse, isLoading, error, selectedResourceId, initialTab]
	);
	
	return (
		<ResultContext.Provider value={value}>
			{children}
		</ResultContext.Provider>
	);
};

// Custom hook to use the result context
export const useResult = (): ResultContextState => {
	const context = useContext(ResultContext);

	if (context === undefined) {
		throw new Error("useResult must be used within a ResultProvider");
	}

	return context;
};
